/**
 * TeamStatsCard
 * Renders team stats data in a chat data card.
 * Displays team record, recent performance, and optional recent game results.
 */

import type { TeamStatsCardData } from '@parlay-picker/shared/chatTypes';
import { parseLeagueKey } from '@parlay-picker/shared/league';
import { findTeam } from '@parlay-picker/shared/teams';
import { Shield, TrendingUp } from 'lucide-react';
import type { ReactElement } from 'react';
import { StatCell } from './cardHelpers';

export function TeamStatsCard({ data }: { data: TeamStatsCardData }): ReactElement {
  const teamAbbr = data.teamAbbr || 'Unknown';
  const league = data.league || '';
  const recentRecord = data.recentRecord || '';
  const totalGames = data.totalGames;
  // Cards saved before the scores were carried have entries with no scores: nothing to show.
  const recentGames = (data.recentGames ?? []).filter(
    (g) => typeof g.teamScore === 'number' && typeof g.opponentScore === 'number',
  );

  const teamInfo = findTeam(teamAbbr, data.leagueKey ?? parseLeagueKey(league) ?? undefined);
  const [wins, losses] = recentRecord.split('-').map(Number);
  const winPct = wins + losses > 0 ? ((wins / (wins + losses)) * 100).toFixed(1) : null;

  return (
    <div className="bg-theme-surface border border-theme-border rounded-xl p-3 space-y-3">
      {/* Header */}
      <div className="flex items-center gap-2">
        {teamInfo?.logo ? (
          <img
            src={teamInfo.logo}
            alt={teamAbbr}
            className="w-5 h-5 object-contain"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
              const fallback = (e.target as HTMLElement).nextElementSibling;
              if (fallback) (fallback as HTMLElement).style.display = 'flex';
            }}
          />
        ) : null}
        <Shield
          className="w-4 h-4 text-theme-primary"
          style={{ display: teamInfo?.logo ? 'none' : undefined }}
        />
        <span className="text-sm font-semibold text-theme-text">{teamInfo?.name || teamAbbr}</span>
        {league && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-theme-bg text-theme-text-secondary">
            {league}
          </span>
        )}
      </div>

      {/* Key stats */}
      <div className="grid grid-cols-3 gap-2">
        {recentRecord && (
          <StatCell
            label={data.recordScope === 'regular_season' ? 'Regular season' : 'Record'}
            value={recentRecord}
          />
        )}
        {winPct != null && (
          <StatCell
            label="Win %"
            value={`${winPct}%`}
            color={Number(winPct) >= 50 ? 'text-theme-success' : 'text-theme-danger'}
          />
        )}
        {data.playoffRecord ? (
          <StatCell label="Playoffs" value={data.playoffRecord} />
        ) : (
          totalGames != null && <StatCell label="Games" value={String(totalGames)} />
        )}
      </div>

      {/* Recent games */}
      {recentGames.length > 0 && (
        <div className="border-t border-theme-border/50 pt-2">
          <div className="flex items-center gap-1 mb-1.5">
            <TrendingUp className="w-3 h-3 text-theme-text-tertiary" />
            <span className="text-[10px] text-theme-text-tertiary">RECENT GAMES</span>
          </div>
          <div className="space-y-1">
            {recentGames.slice(0, 5).map((game, i) => {
              const teamScore = game.teamScore ?? 0;
              const oppScore = game.opponentScore ?? 0;
              const isWin = game.won ?? teamScore > oppScore;
              return (
                <div key={i} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-1.5">
                    <span
                      className={`text-[10px] font-semibold w-4 ${isWin ? 'text-theme-success' : 'text-theme-danger'}`}
                    >
                      {isWin ? 'W' : 'L'}
                    </span>
                    {game.opponentAbbr && (
                      <span className="text-theme-text-secondary">
                        {game.isHome === false ? '@' : 'vs'} {game.opponentAbbr}
                      </span>
                    )}
                  </div>
                  <span className="text-theme-text tabular-nums">
                    {teamScore}-{oppScore}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
